/**
 * Immersive Wizard State Machine
 *
 * @note step_number is explicitly defined rather than derived from array position.
 * This supports A/B testing where steps may be reordered (e.g., skip logo step but keep
 * numbering as 1,2,4,5...). Also handles implicit steps that
 * aren't UI-bound which would break auto-counting. Requires manual maintenance.
 */
import { createMachine, assign } from "xstate";

const MARTIAL_ARTS_GYM_TYPE = "Combat Sports";
export const DEFAULT_COLOR = "#0062FF";

export const WizardStep = {
  Subdomain: "subdomain",
  Color: "color",
  Logo: "logo",
  Schedule: "schedule",
  ClassSchedule: "classSchedule",
  AppointmentTypes: "appointmentTypes",
  AppointmentPackages: "appointmentPackages",
  MembershipPlans: "membershipPlans",
  PointOfSales: "pointOfSales",
  StaffApp: "staffApp",
  MemberApp: "memberApp",
  RankTrackingApp: "rankTrackingApp",
  CommittedClubApp: "committedClubApp",
  Completed: "completed",
} as const;

export type WizardStepType = typeof WizardStep[keyof typeof WizardStep];

export const WizardFormField = {
  Subdomain: "subdomain",
  Color: "color",
  Logo: "logo",
  AppointmentsEnabled: "appointmentsEnabled",
  ClassesEnabled: "classesEnabled",
  SelectedClassTypes: "selectedClassTypes",
  ScheduleScreenshot: "scheduleScreenshot",
  ScheduleScreenshotName: "scheduleScreenshotName",
  ExtractedScheduleData: "extractedScheduleData",
  SelectedAppointmentTypes: "selectedAppointmentTypes",
  AppointmentScheduleScreenshot: "appointmentScheduleScreenshot",
  AppointmentScheduleScreenshotName: "appointmentScheduleScreenshotName",
  ExtractedAppointmentScheduleData: "extractedAppointmentScheduleData",
  SelectedAppointmentPackages: "selectedAppointmentPackages",
  AppointmentPackagesScreenshot: "appointmentPackagesScreenshot",
  MembershipPlans: "membershipPlans",
  MembershipScreenshot: "membershipScreenshot",
  StaffMembers: "staffMembers",
  StaffAppEnabled: "staffAppEnabled",
  MemberAppEnabled: "memberAppEnabled",
} as const;

export type WizardFormFieldType = typeof WizardFormField[keyof typeof WizardFormField];

export interface StaffMember {
  firstName: string;
  lastName: string;
  email: string;
}

interface FormData {
  [WizardFormField.Subdomain]: string;
  [WizardFormField.Color]: string;
  [WizardFormField.Logo]: string | null;
  [WizardFormField.AppointmentsEnabled]: boolean;
  [WizardFormField.ClassesEnabled]: boolean;
  [WizardFormField.SelectedClassTypes]: string[];
  [WizardFormField.ScheduleScreenshot]: string | null;
  [WizardFormField.ScheduleScreenshotName]: string | null;
  [WizardFormField.ExtractedScheduleData]: {
    success?: boolean;
    classes?: Array<{
      title?: string;
      dayOfWeek?: string;
      startTime?: string;
      endTime?: string;
    }>;
  };
  [WizardFormField.SelectedAppointmentTypes]: string[];
  [WizardFormField.AppointmentScheduleScreenshot]: string | null;
  [WizardFormField.AppointmentScheduleScreenshotName]: string | null;
  [WizardFormField.ExtractedAppointmentScheduleData]: {
    success?: boolean;
    classes?: Array<{
      title?: string;
      dayOfWeek?: string;
      startTime?: string;
      endTime?: string;
    }>;
  };
  [WizardFormField.SelectedAppointmentPackages]: string[];
  [WizardFormField.AppointmentPackagesScreenshot]: string | null;
  [WizardFormField.MembershipPlans]: string[];
  [WizardFormField.MembershipScreenshot]: string | null;
  [WizardFormField.StaffMembers]: StaffMember[];
  [WizardFormField.StaffAppEnabled]: boolean;
  [WizardFormField.MemberAppEnabled]: boolean;
}

interface MachineContext {
  formData: FormData;
  currentStep: WizardStepType;
  clientGymType: string | null;
}

type MachineEvents =
  | { type: "CONTINUE" }
  | { type: "SKIP" }
  | { type: "PREV" }
  | { type: "UPDATE_FORM"; data: Partial<FormData> }
  | { type: "UPDATE_CONTEXT"; data: Partial<Pick<MachineContext, 'clientGymType'>> }
  | { type: "COMPLETE" };

export const immersiveWizardMachine = createMachine<MachineContext, MachineEvents>({
  predictableActionArguments: true,
  id: "immersive-wizard-2025",
  initial: WizardStep.Subdomain,
  context: {
    currentStep: WizardStep.Subdomain,
    clientGymType: null,
    formData: {
      [WizardFormField.Subdomain]: "",
      [WizardFormField.Color]: DEFAULT_COLOR,
      [WizardFormField.Logo]: null,
      [WizardFormField.AppointmentsEnabled]: false,
      [WizardFormField.ClassesEnabled]: false,
      [WizardFormField.SelectedClassTypes]: [],
      [WizardFormField.ScheduleScreenshot]: null,
      [WizardFormField.ScheduleScreenshotName]: null,
      [WizardFormField.ExtractedScheduleData]: {},
      [WizardFormField.SelectedAppointmentTypes]: [],
      [WizardFormField.AppointmentScheduleScreenshot]: null,
      [WizardFormField.AppointmentScheduleScreenshotName]: null,
      [WizardFormField.ExtractedAppointmentScheduleData]: {},
      [WizardFormField.SelectedAppointmentPackages]: [],
      [WizardFormField.AppointmentPackagesScreenshot]: null,
      [WizardFormField.MembershipPlans]: [],
      [WizardFormField.MembershipScreenshot]: null,
      [WizardFormField.StaffMembers]: [
        { firstName: "", lastName: "", email: "" }
      ],
      [WizardFormField.StaffAppEnabled]: false,
      [WizardFormField.MemberAppEnabled]: false,
    },
  },
  states: {
    [WizardStep.Subdomain]: {
      meta: {
        id: "step-subdomain",
        step_number: 1,
        title: "Create your unique business URL",
        description: "",
        requiresValidation: true,
        skippable: false,
      },
      entry: assign({
        currentStep: () => WizardStep.Subdomain,
      }),
      on: {
        CONTINUE: WizardStep.Color,
      },
    },
    [WizardStep.Color]: {
      meta: {
        id: "step-color",
        step_number: 2,
        title: "Choose your brand's primary color",
        description: "",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.Color,
      }),
      on: {
        PREV: WizardStep.Subdomain,
        CONTINUE: WizardStep.Logo,
        SKIP: WizardStep.Logo,
      },
    },
    [WizardStep.Logo]: {
      meta: {
        id: "step-logo",
        step_number: 3,
        title: "Add your business logo",
        description: "",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.Logo,
      }),
      on: {
        PREV: WizardStep.Color,
        CONTINUE: WizardStep.Schedule,
        SKIP: WizardStep.Schedule,
      },
    },
    [WizardStep.Schedule]: {
      meta: {
        id: "step-schedule",
        step_number: 4,
        title: "How do you want to manage bookings?",
        description: "",
        requiresValidation: true,
        skippable: false,
      },
      entry: assign({
        currentStep: () => WizardStep.Schedule,
      }),
      on: {
        PREV: WizardStep.Logo,
        CONTINUE: [
          {
            target: WizardStep.ClassSchedule,
            cond: (context) => context.formData[WizardFormField.ClassesEnabled],
          },
          {
            target: WizardStep.AppointmentTypes,
            cond: (context) => context.formData[WizardFormField.AppointmentsEnabled],
          },
          { target: WizardStep.StaffApp },
        ],
      },
    },
    [WizardStep.ClassSchedule]: {
      meta: {
        id: "step-class-schedule",
        step_number: 5,
        title: "Tell us about your class schedule",
        description: "",
        requiresValidation: true,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.ClassSchedule,
      }),
      on: {
        PREV: WizardStep.Schedule,
        CONTINUE: { target: WizardStep.MembershipPlans },
        SKIP: { target: WizardStep.MembershipPlans },
      },
    },
    [WizardStep.MembershipPlans]: {
      meta: {
        id: "step-plan",
        step_number: 6,
        title: "Tell us about your membership options",
        description: "",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.MembershipPlans,
      }),
      on: {
        PREV: { target: WizardStep.ClassSchedule },
        CONTINUE: [
          { target: WizardStep.AppointmentTypes, cond: (context) => context.formData[WizardFormField.AppointmentsEnabled] },
          { target: WizardStep.PointOfSales },
        ],
        SKIP: [
          { target: WizardStep.AppointmentTypes, cond: (context) => context.formData[WizardFormField.AppointmentsEnabled] },
          { target: WizardStep.PointOfSales },
        ],
      },
    },
    [WizardStep.AppointmentTypes]: {
      meta: {
        id: "step-appointment-types",
        step_number: 7,
        title: "Tell us about your appointment types",
        description: "",
        requiresValidation: true,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.AppointmentTypes,
      }),
      on: {
        PREV: [
          {
            target: WizardStep.MembershipPlans,
            cond: (context) => context.formData[WizardFormField.ClassesEnabled],
          },
          {
            target: WizardStep.Schedule,
          }
        ],
        CONTINUE: WizardStep.AppointmentPackages,
        SKIP: WizardStep.AppointmentPackages,
      },
    },
    [WizardStep.AppointmentPackages]: {
      meta: {
        id: "step-appointment-packages",
        step_number: 8,
        title: "Tell us about your appointment packages",
        description: "",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.AppointmentPackages,
      }),
      on: {
        PREV: WizardStep.AppointmentTypes,
        CONTINUE: { target: WizardStep.PointOfSales },
        SKIP: { target: WizardStep.PointOfSales },
      },
    },
    [WizardStep.PointOfSales]: {
      meta: {
        id: "step-point-of-sales",
        step_number: 9,
        title: "Make sales, right where your members are",
        description: "See how Kiosk mode will help you sell",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.PointOfSales,
      }),
      on: {
        PREV: [
          {
            target: WizardStep.AppointmentPackages,
            cond: (context) => context.formData[WizardFormField.AppointmentsEnabled],
          },
          {
            target: WizardStep.MembershipPlans,
            cond: (context) => context.formData[WizardFormField.ClassesEnabled],
          },
          {
            target: WizardStep.Schedule,
          }
        ],
        CONTINUE: WizardStep.StaffApp,
        SKIP: WizardStep.StaffApp,
      },
    },
    [WizardStep.StaffApp]: {
      meta: {
        id: "step-staff-app",
        step_number: 10,
        title: "Send invites to your team members",
        description: "Add your staff members to get started",
        requiresValidation: true,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.StaffApp,
      }),
      on: {
        PREV: WizardStep.PointOfSales,
        CONTINUE: WizardStep.MemberApp,
        SKIP: WizardStep.MemberApp,
      },
    },
    [WizardStep.MemberApp]: {
      meta: {
        id: "step-member-app",
        step_number: 11,
        title: "Connect with your members",
        description: "Use the member app to engage with your members",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.MemberApp,
      }),
      on: {
        PREV: WizardStep.StaffApp,
        CONTINUE: [
          {
            target: WizardStep.RankTrackingApp,
            cond: (context) => context.clientGymType === MARTIAL_ARTS_GYM_TYPE,
          },
          { target: WizardStep.CommittedClubApp },
        ],
        SKIP: [
          {
            target: WizardStep.RankTrackingApp,
            cond: (context) => context.clientGymType === MARTIAL_ARTS_GYM_TYPE,
          },
          { target: WizardStep.CommittedClubApp },
        ],
      },
    },
    [WizardStep.RankTrackingApp]: {
      meta: {
        id: "step-rank-tracking-app",
        step_number: 12,
        title: "Track your martial arts progress",
        description: "Connect with our rank tracking system",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.RankTrackingApp,
      }),
      on: {
        PREV: WizardStep.MemberApp,
        CONTINUE: WizardStep.Completed,
        SKIP: WizardStep.Completed,
      },
    },
    [WizardStep.CommittedClubApp]: {
      meta: {
        id: "step-committed-club-app",
        step_number: 13,
        title: "Build your fitness community",
        description: "Use our tools to increase retention and engagement",
        requiresValidation: false,
        skippable: true,
      },
      entry: assign({
        currentStep: () => WizardStep.CommittedClubApp,
      }),
      on: {
        PREV: WizardStep.MemberApp,
        CONTINUE: WizardStep.Completed,
        SKIP: WizardStep.Completed,
      },
    },
    [WizardStep.Completed]: {
      meta: {
        id: "step-completed",
        title: "Setup Complete",
        description: "Your business is ready to go!",
      },
      entry: assign({
        currentStep: () => WizardStep.Completed,
      }),
      on: {
        PREV: [
          {
            target: WizardStep.RankTrackingApp,
            cond: (context) => context.clientGymType === MARTIAL_ARTS_GYM_TYPE,
          },
          { target: WizardStep.CommittedClubApp },
        ],
      },
    },
  },
  on: {
    UPDATE_FORM: {
      actions: assign({
        formData: (context, event) => ({
          ...context.formData,
          ...event.data,
        }),
      }),
    },
    UPDATE_CONTEXT: {
      actions: assign({
        clientGymType: (context, event) => event.data.clientGymType ?? context.clientGymType,
      }),
    },
  },
});
